import { z } from 'zod'
import { generateUUID } from '../../functions/generateUUID'
import { ApiError, ApiErrorSchema } from '../mng-dare-import/WebMethods'

export type ReportDefinitionType = z.infer<typeof ReportDefinitionType>
export const ReportDefinitionType = z.enum(['Metadata', 'StaticDocument', 'SSRS', 'PowerBI', 'Reflection'])
export type KeyValuePair = z.infer<typeof KeyValuePair>
export const KeyValuePair = z.object({
  Key: z.string(),
  Value: z.string().nullable(),
})
export type ReportDefinitionDto = z.infer<typeof ReportDefinitionDto>
export const ReportDefinitionDto = z.object({
  // Local use only
  _Id: z.string(),

  // For identifying necessary fields
  Type: ReportDefinitionType,
  
  // For: Metadata
  Expiry: z.coerce.date().optional().nullable(),
  ExportFileName: z.string().optional().nullable(),

  // For: StaticDocument
  DocumentPath: z.string().optional().nullable(),
  
  // For: SSRS
  ReportName: z.string().optional().nullable(),
  Parameters: KeyValuePair.array().optional().nullable(),

  // For: Reflection
  MethodDefinition: z.string().optional().nullable(),
})

//#region SSRS
export type SSRSReportParameterType = z.infer<typeof SSRSReportParameterType>
export const SSRSReportParameterType = z.enum(['Boolean', 'DateTime', 'Integer', 'Float', 'String']);
export type SSRSReportParameterVisibility = z.infer<typeof SSRSReportParameterVisibility>
export const SSRSReportParameterVisibility = z.enum(['Visible', 'Hidden', 'Internal'])
export const SSRSReportParameterState = z.enum(['HasValidValue', 'MissingValidValue', 'HasOutstandingDependencies', 'DynamicValuesUnavailable'])

export type SSRSReportParameterDefinition = z.infer<typeof SSRSReportParameterDefinition>
export const SSRSReportParameterDefinition = z.object({
  Name: z.string(),
  ParameterType: SSRSReportParameterType,
  ParameterVisibility: SSRSReportParameterVisibility,
  ParameterState: SSRSReportParameterState,
  ValidValues: z.object({
    Label: z.string(),
    Value: z.string(),
  }).array(),
  Nullable: z.boolean(),
  AllowBlank: z.boolean(),
  MultiValue: z.boolean(),
  Prompt: z.string().nullable(),
  Dependencies: z.string().array(),
  DefaultValues: z.string().nullable().array(),
})

export type SSRSReport = z.infer<typeof SSRSReport>
export const SSRSReport = z.object({
  Id: z.string().uuid(),
  Name: z.string(),
  Description: z.string().nullable(),
  Path: z.string(),
  Size: z.number().int().positive(),
  ModifiedBy: z.string(),
  ModifiedDate: z.coerce.date(),
  CreatedBy: z.string(),
  CreatedDate: z.coerce.date(),
  ParameterDefinitions: SSRSReportParameterDefinition.array(),
})

//#endregion

//#region PowerBI

export type PowerBiReportParameterDefinition = z.infer<typeof PowerBiReportParameterDefinition>
export const PowerBiReportParameterDefinition = z.object({
  Name: z.string(),
  Nullable: z.boolean(),
  Hidden: z.boolean(),
  AllowBlank: z.boolean(),
  DataType: SSRSReportParameterType,
  Prompt: z.string().nullable(),
  MultiValue: z.boolean(),
  ValidValues: z.object({
    Label: z.string().nullable(),
    Value: z.string(),
  }).array().nullable(),
})

export type PowerBiReport = z.infer<typeof PowerBiReport>
export const PowerBiReport = z.object({
  Id: z.string().uuid(),
  Name: z.string(),
  Description: z.string().nullable(),
  WebUrl: z.string(),
})

export type PowerBiReportWithParameters = z.infer<typeof PowerBiReportWithParameters>
export const PowerBiReportWithParameters = PowerBiReport.and(z.object({
  Parameters: PowerBiReportParameterDefinition.array().nullable(),
}))

//#endregion

export type StaticDocumentOption = z.infer<typeof StaticDocumentOption>
export const StaticDocumentOption = z.object({
  DocumentName: z.string(),
  DocumentPath: z.string(),
})

export type ReflectionMethodParameterOption = z.infer<typeof ReflectionMethodParameterOption>
export const ReflectionMethodParameterOption = z.object({
  Name: z.string(),
  ClrType: z.string(),
})

export type ReflectionMethodOption = z.infer<typeof ReflectionMethodOption>
export const ReflectionMethodOption = z.object({
  MethodDefinition: z.string(),
  DisplayName: z.string(),
  Parameters: ReflectionMethodParameterOption.array(),
})

const NBSP = '\u00A0';

export class ReportsAPI {
  baseURL: URL;
  constructor(base: URL) {
    this.baseURL = base;
  }

  private async _handleError(res: Response) {
    if (res.ok) return;

    let typedError: ApiError | undefined = undefined;
    try {
      const json = await res.json();
      typedError = await ApiErrorSchema.parseAsync(json);
    }
    catch {}
    if (typedError !== undefined) {
      throw typedError;
    }
    else {
      throw res;
    }
  }

  public async GetSSRSReports(): Promise<SSRSReport[]> {
    const res = await fetch(`${this.baseURL.href}/api/Reports/SSRS/GetReports`);
    await this._handleError(res);
    const data = await res.json();
    return await SSRSReport.array().parseAsync(data);
  }
  //PowerBiReport
  public async GetPowerBIReports(): Promise<PowerBiReportWithParameters[]> {
    const res = await fetch(`${this.baseURL.href}/api/Reports/PowerBI/GetPaginatedReports`);
    await this._handleError(res);
    const data = await res.json();
    return await PowerBiReportWithParameters.array().parseAsync(data);
  }
  public async GetPowerBIReportWithParameter(reportID: string): Promise<PowerBiReportWithParameters> {
    const res = await fetch(`${this.baseURL.href}/api/Reports/PowerBI/GetParameters/${encodeURIComponent(reportID)}`);
    await this._handleError(res);
    const data = await res.json();
    return await PowerBiReportWithParameters.parseAsync(data);
  }
  public async GetStaticDocuments(): Promise<StaticDocumentOption[]> {
    const res = await fetch(`${this.baseURL.href}/api/Reports/GetStaticDocuments`);
    await this._handleError(res);
    const data = await res.json();
    return await StaticDocumentOption.array().parseAsync(data);
  }
  public async GetEligibleReflectionMethods(): Promise<ReflectionMethodOption[]> {
    const res = await fetch(`${this.baseURL.href}/api/Reports/GetEligibleReflectionMethods`);
    await this._handleError(res);
    const data = await res.json();
    const parsed = await ReflectionMethodOption.array().parseAsync(data);
    for (let m of parsed) {
      m.MethodDefinition = m.MethodDefinition.replaceAll(' ', NBSP);
    }
    return parsed;
  }
  public async EncodeReportDefinition(definitions: ReportDefinitionDto[]): Promise<string> {
    for(let def of definitions) {
      if (def.MethodDefinition) {
        def.MethodDefinition = def.MethodDefinition?.replaceAll(NBSP, ' ')
      }
    }
    const res = await fetch(`${this.baseURL.href}/api/Reports/EncodeReportDefinition`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(definitions),
    });
    await this._handleError(res);
    const data = await res.json();
    const parsed = await z.object({ Encoded: z.string() }).parseAsync(data);
    return parsed.Encoded;
  }
  public async ReduceReflections(definitions: ReportDefinitionDto[]): Promise<ReportDefinitionDto[]> {
    for(let def of definitions) {
      if (def.MethodDefinition) {
        def.MethodDefinition = def.MethodDefinition?.replaceAll(NBSP, ' ');
      }
    }
    const res = await fetch(`${this.baseURL.href}/api/Reports/ReduceReflections`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(definitions),
    });
    await this._handleError(res);
    const data = await res.json();
    const parsed = await z.object({ Definitions: ReportDefinitionDto.partial({ _Id: true }).array() }).parseAsync(data);
    for(let i = 0; i < parsed.Definitions.length; i++) {
      parsed.Definitions[i]._Id = generateUUID();
      if (parsed.Definitions[i].MethodDefinition) {
        parsed.Definitions[i].MethodDefinition = parsed.Definitions[i].MethodDefinition?.replaceAll(' ', NBSP);
      }
    }
    return await ReportDefinitionDto.array().parseAsync(parsed.Definitions)
  }
  public async DecodeReportDefinition(definition: string): Promise<ReportDefinitionDto[]> {
    const params = new URLSearchParams({
    });
    const res = await fetch(`${this.baseURL.href}/api/Reports/DecodeReportDefinition?${params.toString()}`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(definition),
    });
    await this._handleError(res);
    const data = await res.json();
    // The server doesn't have an "_Id" field, so we need to add one ourselves
    const parsed = await ReportDefinitionDto.partial({ _Id: true }).array().parseAsync(data)
    for(let i = 0; i < parsed.length; i++) {
      parsed[i]._Id = generateUUID();
      if (parsed[i].MethodDefinition) {
        parsed[i].MethodDefinition = parsed[i].MethodDefinition?.replaceAll(' ', NBSP);
      }
    }
    return await ReportDefinitionDto.array().parseAsync(parsed)
  }
  /** Returns a FileReferenceID */
  public async RunReport(definition: string): Promise<string> {
    const params = new URLSearchParams({
      interactive: 'false',
    });
    const res = await fetch(`${this.baseURL.href}/api/Reports/RunReport?${params.toString()}`, {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(definition),
      redirect: 'manual'
    });
    await this._handleError(res);
    const data = await res.json();
    const parsed = await z.object({ FileReferenceID: z.string() }).parseAsync(data);
    return parsed.FileReferenceID;
  }
}
